// As saídas de quem não conseguiu entrar.
//
// Aparece SÓ depois de uma tentativa falha: oferecer "esqueci a senha" antes de a
// pessoa tentar é ruído, e empurra para o caminho lento quem ia acertar de primeira.
//
// ⚠️ As duas ações respondem SEMPRE a mesma coisa, exista o e-mail ou não. Dizer
// "e-mail não cadastrado" seria entregar a lista de clientes da Piloto a quem testar
// endereços no formulário. Quem errou a própria senha já sabe que a conta existe, e
// não perde nada com a resposta muda.

import React, { useState } from 'react';
import {
  Alert, AlertIcon, Box, Button, Divider, HStack, Input, Stack, Text, Textarea,
  useToast,
} from '@chakra-ui/react';
import { Mail, MessageSquareWarning, UserRoundX } from 'lucide-react';
import { api } from '../services/api';

interface Props {
  email: string;
  aoTrocarConta: () => void;
}

export default function AjudaAcesso({ email, aoTrocarConta }: Props) {
  const toast = useToast();
  const [enviandoLink, setEnviandoLink] = useState(false);
  const [reportando, setReportando] = useState(false);
  const [formReporte, setFormReporte] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [emailReporte, setEmailReporte] = useState(email);

  const pedirLink = async () => {
    if (!email.trim()) {
      toast({ title: 'Escreva seu e-mail no campo acima primeiro', status: 'warning', duration: 3000 });
      return;
    }
    setEnviandoLink(true);
    try {
      await api.post('api/auth/link', { email });
      // Mensagem propositalmente vaga quanto à existência da conta — ver o cabeçalho.
      toast({
        title: 'Se houver conta com esse e-mail, o link já saiu',
        description: 'Confira a caixa de entrada e o spam. O link vale 1 hora e serve uma vez.',
        status: 'success', duration: 8000, isClosable: true,
      });
    } catch {
      toast({ title: 'Não deu para pedir o link agora', status: 'error', duration: 4000 });
    } finally { setEnviandoLink(false); }
  };

  const reportar = async () => {
    setReportando(true);
    try {
      await api.post('api/auth/reportar', { email: emailReporte, mensagem });
      toast({
        title: 'Reporte enviado',
        description: 'Chegou para quem administra o app. Se precisar de resposta, deixe um contato na mensagem.',
        status: 'success', duration: 7000, isClosable: true,
      });
      setMensagem(''); setFormReporte(false);
    } catch {
      toast({ title: 'Não deu para enviar o reporte', status: 'error', duration: 4000 });
    } finally { setReportando(false); }
  };

  return (
    <Box mt={5}>
      <Divider mb={4} />
      <Alert status="info" borderRadius="md" fontSize="sm" mb={3}>
        <AlertIcon />
        <Box>
          <Text fontWeight="medium">Mandamos um e-mail para você.</Text>
          <Text>Ele tem três opções: entrar sem senha, entrar e trocar a senha, ou
            avisar que não foi você. Confira a caixa de entrada e o spam.</Text>
        </Box>
      </Alert>

      <Stack spacing={2}>
        <Button size="sm" variant="outline" leftIcon={<Mail size={15} />}
          onClick={pedirLink} isLoading={enviandoLink} loadingText="Enviando…">
          Receber link de acesso por e-mail
        </Button>

        <Button size="sm" variant="ghost" leftIcon={<UserRoundX size={15} />}
          onClick={aoTrocarConta}>
          Entrar com outra conta
        </Button>

        {!formReporte ? (
          <Button size="sm" variant="ghost" colorScheme="orange"
            leftIcon={<MessageSquareWarning size={15} />} onClick={() => setFormReporte(true)}>
            Reportar um problema
          </Button>
        ) : (
          <Box borderWidth="1px" borderRadius="md" p={3}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>O que aconteceu?</Text>
            <Input size="sm" mb={2} value={emailReporte} placeholder="seu e-mail ou contato"
              onChange={(e) => setEmailReporte(e.target.value)} />
            <Textarea size="sm" mb={2} rows={3} value={mensagem}
              placeholder="Ex.: recebi o link mas ele diz que expirou"
              onChange={(e) => setMensagem(e.target.value)} />
            <HStack>
              <Button size="xs" colorScheme="orange" onClick={reportar} isLoading={reportando}>
                Enviar
              </Button>
              <Button size="xs" variant="ghost" onClick={() => setFormReporte(false)}>
                Cancelar
              </Button>
            </HStack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
